import type {
  BookingFlowClientEventInput,
  BookingFlowClientEventType,
  BookingFlowClientEventInputEventTypeMap,
} from './bookingFlowAnalyticsTypes.ts';
import { BOOKING_FLOW_ANALYTICS_SCHEMA_VERSION } from './bookingFlowAnalyticsTypes.ts';
import { validateBookingFlowClientEventInput, type BookingFlowAnalyticsValidationIssue } from './bookingFlowAnalyticsValidation.ts';
import type { BookingFlowIdentityState } from './bookingFlowAnalyticsIdentity.ts';
import type { ReservationAnalyticsSession } from './analyticsTypes.ts';

export interface BookingFlowAnalyticsBuildContext {
  unitId: string;
  session: ReservationAnalyticsSession;
  state: BookingFlowIdentityState;
  eventId: string;
  occurredAtClient: string;
  clientTimezone: string;
}

export type BookingFlowAnalyticsBuildResult<T extends BookingFlowClientEventInput = BookingFlowClientEventInput> =
  | { valid: true; event: T }
  | { valid: false; issues: BookingFlowAnalyticsValidationIssue[] };

type ClientEventByType<T extends BookingFlowClientEventType> = BookingFlowClientEventInputEventTypeMap[T];

type ClientEventDraft<T extends BookingFlowClientEventType> = Omit<
  ClientEventByType<T>,
  'schemaVersion' | 'authority' | 'eventId' | 'unitId' | 'visitorId' | 'sessionId' | 'bookingFlowId' | 'occurredAtClient' | 'clientTimezone' | 'idempotencyKey'
>;

const idempotencyKey = (input: { unitId: string; bookingFlowId: string; eventType: string; selectionRevision?: number; availabilityRevision?: number; submitAttemptId?: string; discriminator?: string }) => [
  input.unitId,
  input.bookingFlowId,
  input.eventType,
  input.selectionRevision !== undefined ? `selection-${input.selectionRevision}` : null,
  input.availabilityRevision !== undefined ? `availability-${input.availabilityRevision}` : null,
  input.submitAttemptId ? `submit-${input.submitAttemptId}` : null,
  input.discriminator ? `detail-${input.discriminator}` : null,
].filter(Boolean).join(':');

const idempotencyDiscriminator = (draft: { eventType: string; payload: any }) => {
  if (draft.eventType === 'reservation_validation_blocked') return `reason-${String(draft.payload.reasonCode)}`;
  if (draft.eventType === 'reservation_submit_transport_failed') return `transport-${String(draft.payload.transportFailureKind)}`;
  return undefined;
};

export const buildCanonicalBookingFlowClientEventIdempotencyKey = (event: BookingFlowClientEventInput) => idempotencyKey({
  unitId: event.unitId,
  bookingFlowId: event.bookingFlowId,
  eventType: event.eventType,
  selectionRevision: 'selectionRevision' in event ? event.selectionRevision : undefined,
  availabilityRevision: 'availabilityRevision' in event ? event.availabilityRevision : undefined,
  submitAttemptId: 'submitAttemptId' in event ? event.submitAttemptId : undefined,
  discriminator: idempotencyDiscriminator(event as any),
});

export const buildBookingFlowClientEvent = <T extends BookingFlowClientEventType>(
  context: BookingFlowAnalyticsBuildContext,
  draft: ClientEventDraft<T>,
): BookingFlowAnalyticsBuildResult<ClientEventByType<T>> => {
  const event = {
    schemaVersion: BOOKING_FLOW_ANALYTICS_SCHEMA_VERSION,
    authority: 'client',
    eventId: context.eventId,
    eventType: draft.eventType,
    unitId: context.unitId,
    visitorId: context.session.visitorId,
    sessionId: context.session.sessionId,
    bookingFlowId: context.state.bookingFlowId,
    occurredAtClient: context.occurredAtClient,
    clientTimezone: context.clientTimezone,
    idempotencyKey: idempotencyKey({
      unitId: context.unitId,
      bookingFlowId: context.state.bookingFlowId,
      eventType: draft.eventType,
      selectionRevision: 'selectionRevision' in draft ? Number(draft.selectionRevision) : undefined,
      availabilityRevision: 'availabilityRevision' in draft ? Number(draft.availabilityRevision) : undefined,
      submitAttemptId: 'submitAttemptId' in draft ? String(draft.submitAttemptId) : undefined,
      discriminator: idempotencyDiscriminator(draft as any),
    }),
    ...draft,
  };

  const validation = validateBookingFlowClientEventInput(event);
  return validation.valid === true
    ? { valid: true, event: validation.value as ClientEventByType<T> }
    : { valid: false, issues: validation.issues };
};

